import React from "react";
import { motion } from "framer-motion";
import { useLanguage } from "../../contexts/LanguageContext";
import { useT } from "../../translations";

const MenuDrawer = ({ open, onClose, settings, onOpenWifi }) => {
  const { lang, setLang } = useLanguage();
  const t = useT(lang);

  return (
    <>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-40 bg-black/40"
          data-testid="drawer-backdrop"
        />
      )}
      <motion.aside
        initial={{ x: "-100%" }}
        animate={{ x: open ? 0 : "-100%" }}
        transition={{ type: "spring", damping: 30, stiffness: 280 }}
        className="fixed top-0 left-0 bottom-0 z-50 w-[82%] max-w-xs bg-zbeach-surface shadow-2xl flex flex-col"
        data-testid="menu-drawer"
      >
        <div className="px-6 pt-6 pb-4 border-b border-zbeach-gold/15">
          <div className="flex items-center justify-between">
            {settings?.logo ? (
              <img src={settings.logo} alt={settings?.businessName} className="h-10 w-auto object-contain" />
            ) : (
              <div className="font-serif text-2xl text-gold-gradient font-semibold tracking-wider">{settings?.businessName || "Z BEACH"}</div>
            )}
            <button onClick={onClose} className="w-9 h-9 rounded-full bg-zbeach-bg flex items-center justify-center" data-testid="drawer-close">
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
          <p className="font-hand text-xl text-zbeach-gold mt-1">
            {lang === "en" ? settings?.heroSubtitleEN : settings?.heroSubtitleTR}
          </p>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-2">
          {/* Language */}
          <div className="px-2 pt-1 pb-2 text-[11px] uppercase tracking-wider text-zbeach-muted">Dil / Language</div>
          <div className="flex gap-2 px-2">
            {["tr", "en"].map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                className={`flex-1 py-2 rounded-full text-sm font-medium transition ${lang === l ? "bg-zbeach-turquoise text-white" : "bg-zbeach-bg text-zbeach-muted"}`}
                data-testid={`drawer-lang-${l}`}
              >
                {l === "tr" ? "Türkçe" : "English"}
              </button>
            ))}
          </div>

          <div className="px-2 pt-5 pb-2 text-[11px] uppercase tracking-wider text-zbeach-muted">{lang === "en" ? "Quick Info" : "Hızlı Bilgi"}</div>
          {settings?.activeFeatures?.wifi !== false && settings?.wifiName && (
            <DrawerLink icon="fa-wifi" label={t.wifiTitle} sub={settings.wifiName} onClick={() => { onClose(); onOpenWifi(); }} testid="drawer-wifi" />
          )}
          {settings?.instagramUrl && (
            <DrawerLink icon="fa-instagram" brand label="Instagram" sub={settings.instagramUrl.replace(/https?:\/\/(www\.)?/, "")} onClick={() => window.open(settings.instagramUrl, "_blank")} testid="drawer-instagram" />
          )}
          {settings?.phone && (
            <DrawerLink icon="fa-phone" label={lang === "en" ? "Phone" : "Telefon"} sub={settings.phone} onClick={() => window.location.href = `tel:${settings.phone}`} testid="drawer-phone" />
          )}
          {settings?.whatsapp && (
            <DrawerLink icon="fa-whatsapp" brand label="WhatsApp" sub={settings.whatsapp} onClick={() => window.open(`https://wa.me/${settings.whatsapp.replace(/\D/g, "")}`, "_blank")} testid="drawer-whatsapp" />
          )}
          {settings?.email && (
            <DrawerLink icon="fa-envelope" label="E-posta" sub={settings.email} onClick={() => window.location.href = `mailto:${settings.email}`} testid="drawer-email" />
          )}
          {settings?.address && (
            <DrawerLink icon="fa-location-dot" label={lang === "en" ? "Address" : "Adres"} sub={settings.address} onClick={() => settings.mapsUrl && window.open(settings.mapsUrl, "_blank")} testid="drawer-address" />
          )}
        </div>

        <div className="px-6 py-4 border-t border-zbeach-gold/15 text-center">
          <div className="font-hand text-zbeach-gold text-lg">{t.thanks}</div>
          <div className="text-[11px] text-zbeach-muted tracking-widest uppercase mt-1">{lang === "en" ? settings?.footerTextEN : settings?.footerTextTR}</div>
        </div>
      </motion.aside>
    </>
  );
};

const DrawerLink = ({ icon, brand, label, sub, onClick, testid }) => (
  <button onClick={onClick} className="w-full flex items-center gap-3 px-3 py-3 rounded-2xl hover:bg-zbeach-bg transition text-left" data-testid={testid}>
    <div className="w-10 h-10 rounded-xl bg-zbeach-bg flex items-center justify-center text-zbeach-turquoise">
      <i className={`${brand ? "fa-brands" : "fa-solid"} ${icon}`} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium text-zbeach-dark">{label}</div>
      <div className="text-xs text-zbeach-muted truncate">{sub}</div>
    </div>
    <i className="fa-solid fa-chevron-right text-zbeach-gold/50 text-xs" />
  </button>
);

export default MenuDrawer;
